import api from "ApiClient";
import { Alert, Button, Modal, Upload } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./style.module.css";
import cloudUpload from "assets/icons/cloud-upload.svg";
import _, { capitalize } from "lodash";
import { showSuccessNotification } from "utils/hooks/handleNotification";
import { ApiURL } from "utils/Actions";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { fetchStudentTable } from "redux/reducers/StudentsTableSlice";
import { EASDocListType, fetchEASSummary, resetEASResult } from "redux/reducers/EASSlice";
import FileItem from "components/common/file-item";
import { validateFile } from "utils/hooks/documentActions";

type UploadDocModalPropType = {
  isopen: boolean;
  onCloseModal: () => void;
  student: any;
};

const UploadDocModal: React.FC<UploadDocModalPropType> = ({ isopen, onCloseModal, student }) => {
  const dispatch = useAppDispatch();
  const { data = [] } = useAppSelector(state => state.eas);
  const { env } = useAppSelector(state => state.globalUI);
  const { t } = useTranslation();
  const [fileList, setFileList] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitErrors, setSubmitErrors] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<any[]>([]);

  const closeModal = () => {
    setFileList([]);
    setIsSubmitting(false);
    setSubmitErrors([]);
    setValidationErrors([]);
    onCloseModal();
  };

  const removeValidationErrors = (error: any) => {
    setValidationErrors((prev: any[]) => {
      return prev.filter(e => e.id != error.id);
    });
  };

  const updateValidationErrors = (id: string, msg: React.ReactNode) => {
    setValidationErrors((prev: any[]) => {
      return [...prev, { id, msg }];
    });
  };

  const updateSubmitErrors = (id: string, msg: string) => {
    setSubmitErrors((prev: any[]) => {
      return [...prev, { id, msg }];
    });
  };

  const removeFile = (file: any) => {
    setFileList((prev: any[]) => {
      return prev.filter(e => e != file);
    });
  };

  const uploadProps = {
    multiple: true,
    fileList,
    beforeUpload: (file: any) => {
      const { valid, errors } = validateFile(file);
      if (valid) {
        setValidationErrors([]);
        setFileList((prev: any[]) => [...prev, file]);
      } else {
        errors.forEach(({ id, msg }) => {
          updateValidationErrors(id, msg);
        });
      }
      return false;
    },
    itemRender: (originNode: any, file: any) => <FileItem file={file} removeFile={removeFile} />
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    if (fileList.length > 0 && student.refNum) {
      const allPromise = Promise.all(
        fileList.map(async file => {
          const formData = new FormData();
          formData.append("file", file);
          const res = await api.post(`${ApiURL.uploadEasDoc}/${student.refNum}`, formData);
          return res;
        })
      );

      try {
        const results = await allPromise;
        results.forEach((result: any, i: any) => {
          if (result.status !== 201) {
            const id = _.uniqueId("submitError-");
            const msg = `An error occurred while uploading ${fileList[i].name}. Please try again.`;
            updateSubmitErrors(id, msg);
            return false;
          }
        });
        const addedFiles: EASDocListType[] = fileList.map(f => ({
          documentName: f.name as string,
          fileReference: ""
        }));
        showSuccessNotification({ message: "Upload successful" });
        const updatedEASResult = [...data];
        const matchingStudentIndex = data.findIndex(d => d.studentNumber === student.studentNumber);
        if (matchingStudentIndex === 0 || matchingStudentIndex > -1) {
          const matchingStudent = data[matchingStudentIndex];
          const oldDocuments = matchingStudent.easApplicantDocuments || [];
          const easApplicantDocuments: EASDocListType[] = [...oldDocuments, ...addedFiles];
          const newStudent = { ...matchingStudent, easApplicantDocuments };
          updatedEASResult.splice(matchingStudentIndex, 1, newStudent);
          dispatch(resetEASResult(updatedEASResult));
        }
        closeModal();

        // Refresh data
        dispatch(fetchEASSummary());
        dispatch(fetchStudentTable({ source: env }));
      } catch (error: any) {
        setIsSubmitting(false);
        const id = _.uniqueId("apiError-");
        const msg = "An error occurred while uploading files. Please try again.";
        updateSubmitErrors(id, msg);
        throw new Error(error);
      }
    }
  };

  return (
    <Modal
      className="upload-modal"
      title={<div className={styles["modal-title"]}>{t("uploadEIS.title")}</div>}
      open={isopen}
      onCancel={closeModal}
      closable={!isSubmitting}
      maskClosable={false}
      footer={[
        <Button
          key="back"
          disabled={isSubmitting}
          onClick={closeModal}
          aria-label="Cancel the submission and close the modal"
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isSubmitting}
          disabled={isSubmitting || fileList.length == 0}
          onClick={handleSubmit}
        >
          Upload
        </Button>
      ]}
    >
      <div className={styles["name-banner"]}>
        <div>Upload for</div>
        <span className="display-h2-small">
          {capitalize(student?.givenName1)} {capitalize(student?.familyName)}
        </span>
      </div>

      {submitErrors.length > 0 &&
        submitErrors.map((error: any) => (
          <Alert
            key={error.id}
            type="error"
            message={error.msg}
            showIcon
            className={styles["error-alert"]}
          />
        ))}

      {validationErrors.length > 0 &&
        validationErrors.map((error: any) => (
          <Alert
            key={error.id}
            type="error"
            message={error.msg}
            showIcon
            closable={true}
            className={styles["error-alert"]}
            onClose={() => removeValidationErrors(error)}
          />
        ))}

      <div className={styles["modal-body-wrapper"]}>
        <div className={styles["desc-text"]}>{t("uploadEIS.desc")}</div>
        <Upload.Dragger {...uploadProps} name="files">
          <img src={cloudUpload} alt="" />
          <p className="bottom-zero">{t("uploadEIS.drager.title")}</p>
          <p className="display-tab-typo">{t("uploadEIS.drager.desc")}</p>
        </Upload.Dragger>
      </div>
    </Modal>
  );
};

export default UploadDocModal;
